import { useEffect, useState } from "react";
import {
  api,
  ApiError,
  newIdempotencyKey,
  type AgentConfigBody,
  type ArchetypeSummary,
  type AxisValues,
  type Channel,
  type ExpertSummary,
  type MeetingChannels,
  type Proactiveness,
} from "../api";
import { previewLineFor } from "../i18n/previewLines";

type Tone = "consultant" | "advisor" | "reviewer";

// Reverse mapping: archetype id → legacy ExpertTone. Used to populate
// the back-compat `expert_tones` body field so worker paths that still
// read it (until §8 lands) keep behaving as before. Mirrors the
// inverse of `legacy_tone_to_archetype` in crates/core/src/meeting.rs.
function archetypeToLegacyTone(expertId: string, archetypeId: string): Tone {
  if (expertId === "software-spec-author") {
    if (archetypeId === "spec_editor") return "consultant";
    if (archetypeId === "architect") return "reviewer";
    return "advisor"; // tech_lead + anything else
  }
  if (expertId === "aws-solutions-architect") {
    if (archetypeId === "cost_sentinel") return "reviewer";
    return "advisor"; // whiteboarder + war_reviewer
  }
  return "advisor";
}
import { useLocale } from "../i18n/useT";
import { ExpertMark } from "../components/design/ExpertMark";
import { LangPill } from "../components/design/LangPill";
import { VoiceSignature } from "../components/design/VoiceSignature";
import { Icon } from "../components/design/Icon";
import { AgentConfigCard } from "../components/configure/AgentConfigCard";
import { FineTuneDisclosure } from "../components/configure/FineTuneDisclosure";
import { defaultsFor } from "../lib/agent_defaults";
import { navigate, routes } from "../router";

const MAX_PICKS = 1;
const LANG_KEY = "recspec.lang";
const TONE_KEY = "recspec.tone";

type Lang = "EN" | "PT" | "DE";
type TriggerKind = "paste" | "start";

const LANGUAGES: ReadonlyArray<{ id: Lang; label: string }> = [
  { id: "EN", label: "English" },
  { id: "PT", label: "Português" },
  { id: "DE", label: "Deutsch" },
];

export function MeetingNew() {
  const { locale } = useLocale();
  const [lang, setLang] = useState<Lang>(() => {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === "EN" || stored === "PT" || stored === "DE") return stored;
    return locale;
  });
  const [trigger, setTrigger] = useState<TriggerKind>("start");
  const [url, setUrl] = useState("");
  const [experts, setExperts] = useState<ExpertSummary[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [tone, setTone] = useState<Tone>(() => {
    const stored = localStorage.getItem(TONE_KEY);
    if (stored === "consultant" || stored === "advisor" || stored === "reviewer") {
      return stored;
    }
    return "advisor";
  });
  const [channels] = useState<MeetingChannels>({
    qa: true,
    frames: true,
    voice: "highlights",
  });

  // ─── Configure-agent state (extract-experts-to-yaml §10) ──────────
  // Tracks the picker's per-meeting agent configuration: which archetype
  // is selected for the picked expert, current axis values (defaulted
  // from the archetype's baseline + register overlay), and the behavior
  // controls. Initialized when the user picks an expert; re-initialized
  // when they pick a different one.
  const [archetypeId, setArchetypeId] = useState<string | null>(null);
  const [axes, setAxes] = useState<AxisValues>({
    assertiveness: "recommends",
    responsiveness: "balanced",
    evidence: "cited",
  });
  const [proactiveness, setProactiveness] = useState<Proactiveness>("hold");
  const [channel, setChannel] = useState<Channel>("chat");
  const [frames, setFrames] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
  }, [lang]);
  useEffect(() => {
    localStorage.setItem(TONE_KEY, tone);
  }, [tone]);

  useEffect(() => {
    let alive = true;
    api
      .listExperts(lang)
      .then((list) => {
        if (!alive) return;
        setExperts(list);
        // Default-pick AWS SA + System Design Reviewer if they exist & are
        // language-supported, matching the design's prototype defaults.
        const defaults = ["aws-solutions-architect", "system-design-reviewer"];
        const initial: string[] = [];
        for (const id of defaults) {
          const e = list.find((x) => x.id === id);
          if (e && e.supported_languages.includes(lang)) {
            initial.push(pinFor(e));
            if (initial.length === MAX_PICKS) break;
          }
        }
        if (initial.length > 0) setPicked(initial);
      })
      .catch((e) => {
        if (!alive) return;
        setError(
          e instanceof ApiError
            ? `Could not load experts: ${e.message}`
            : "Could not load experts",
        );
      });
    return () => {
      alive = false;
    };
    // Re-fetch when language changes so the picker chips show
    // localized display_name / persona_summary per the api's lang
    // query param.
  }, [lang]);

  // When language changes, drop any picked experts that no longer support it.
  useEffect(() => {
    if (!experts) return;
    setPicked((prev) =>
      prev.filter((pin) => {
        const id = pin.split("@")[0];
        const e = experts.find((x) => x.id === id);
        return e && e.supported_languages.includes(lang);
      }),
    );
  }, [lang, experts]);

  // When the picked expert changes, re-seed archetype + axes from the
  // first archetype the picked expert declares (or its is_default).
  useEffect(() => {
    if (!experts || picked.length === 0) {
      setArchetypeId(null);
      return;
    }
    const id = picked[0].split("@")[0];
    const expert = experts.find((x) => x.id === id);
    const archetypes = expert?.archetypes ?? [];
    if (archetypes.length === 0) {
      setArchetypeId(null);
      return;
    }
    const def = archetypes.find((a) => a.is_default) ?? archetypes[0];
    setArchetypeId(def.id);
    setAxes(def.baseline);
  }, [experts, picked]);

  const isPicked = (e: ExpertSummary) => picked.includes(pinFor(e));
  // Single-agent cap (`cap-meeting-to-single-agent`): clicking an expert
  // either deselects it (if already picked) or replaces the current pick.
  const togglePick = (e: ExpertSummary) => {
    const pin = pinFor(e);
    setPicked((prev) => {
      if (prev.includes(pin)) return [];
      return [pin];
    });
  };

  const pickedExperts = (experts ?? []).filter((e) => isPicked(e));
  const canLaunch =
    picked.length >= 1 && (trigger === "start" || url.trim().length > 5);

  async function submit() {
    if (busy || !canLaunch) return;
    setError(null);
    setBusy(true);
    try {
      // Build the new agent body when the picker has resolved an
      // archetype. Falls back to the legacy expert_tones-only path
      // when no archetype is available (e.g. api booted without
      // EXPERTS_DIR — `expert.archetypes` is empty).
      const expertBareId = picked[0]?.split("@")[0] ?? "";
      const agent: AgentConfigBody | undefined = archetypeId
        ? {
            expert_id: expertBareId,
            archetype_id: archetypeId,
            axes,
            proactiveness,
            channel,
            frames,
          }
        : undefined;
      // Derive the legacy tone from the archetype so worker paths
      // that still read `expert_tones` (pre-§8) keep behaving sanely.
      const effectiveTone = archetypeId
        ? archetypeToLegacyTone(expertBareId, archetypeId)
        : tone;
      // The new agent flow ignores `channels` for QA/voice (those move
      // to per-agent proactiveness/channel). Frames still rides on
      // `channels.frames` until the worker path migrates.
      const legacyChannels: MeetingChannels = {
        qa: channels.qa,
        frames,
        voice: channels.voice,
      };
      const resp = await api.createMeeting({
        meet_url:
          trigger === "start"
            ? "https://meet.google.com/placeholder"
            : url.trim(),
        idempotency_key: newIdempotencyKey(),
        expert_ids: picked,
        channels: legacyChannels,
        trigger_kind: trigger,
        language: lang,
        agent,
        locale: typeof navigator !== "undefined" ? navigator.language : undefined,
      });
      const tones: Record<string, Tone> = {};
      for (const pin of picked) {
        tones[pin.split("@")[0]] = effectiveTone;
      }
      try {
        await api.updateExpertTones(resp.meeting_id, tones);
      } catch {
        // Non-fatal.
      }
      navigate(routes.meeting(resp.meeting_id));
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="recspec-picker"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 360px",
        minHeight: "100%",
      }}
    >
      {/* Left: configurator */}
      <div style={{ borderRight: "1px solid var(--rule)" }}>
        <header
          style={{
            padding: "28px 32px 18px",
            borderBottom: "1px solid var(--rule)",
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            /meetings/new
          </div>
          <h1
            className="serif"
            style={{
              margin: 0,
              fontSize: 40,
              fontWeight: 400,
              letterSpacing: "-0.01em",
              lineHeight: 1.05,
            }}
          >
            Assemble{" "}
            <em style={{ fontStyle: "italic", color: "var(--ink-3)" }}>
              your panel.
            </em>
          </h1>
          <div className="dim" style={{ marginTop: 8, maxWidth: 720 }}>
            Pick one or two experts. Each listens through a different lens —
            domain on one axis, tone on the other. The bot joins as a single
            participant; the experts share the desk.
          </div>
        </header>

        <Step n="1" title="Meeting language">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {LANGUAGES.map((l) => {
              const on = lang === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setLang(l.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    background: on ? "var(--ink)" : "var(--card)",
                    color: on ? "var(--paper)" : "var(--ink)",
                    border: `1px solid ${on ? "var(--ink)" : "var(--rule)"}`,
                    borderRadius: 8,
                    cursor: "pointer",
                    minWidth: 180,
                    fontFamily: "inherit",
                    fontSize: 14,
                  }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 11,
                      letterSpacing: ".08em",
                      background: on
                        ? "rgba(255,255,255,.12)"
                        : "var(--paper-2)",
                      padding: "2px 6px",
                      borderRadius: 4,
                    }}
                  >
                    {l.id}
                  </span>
                  <span style={{ fontWeight: 500 }}>{l.label}</span>
                </button>
              );
            })}
          </div>
          <div className="dim" style={{ marginTop: 10, fontSize: 12 }}>
            Meeting language drives chat-post and artifact language. Some
            experts only speak certain languages — they'll dim out below.
          </div>
        </Step>

        <Step n="2" title="How does the bot join">
          <div
            className="recspec-roster-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <TriggerCard
              active={trigger === "start"}
              onClick={() => setTrigger("start")}
              icon="spark"
              title="Start a new meeting"
              desc="RecSpec creates a Google Meet, dispatches the bot, hands you a join link. Bot present from t=0."
            />
            <TriggerCard
              active={trigger === "paste"}
              onClick={() => setTrigger("paste")}
              icon="link"
              title="Paste an existing link"
              desc="You already started a Meet? Drop the URL — the bot joins as a participant within ~10s."
            />
          </div>
          {trigger === "paste" && (
            <div style={{ marginTop: 12 }}>
              <div
                className="mono dim"
                style={{ fontSize: 11, marginBottom: 6 }}
              >
                MEET URL
              </div>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://meet.google.com/abc-defg-hij"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--rule)",
                  background: "var(--card)",
                  fontFamily: "var(--mono)",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </Step>

        <Step n="3" title="The roster" sub={`Pick exactly one. ${picked.length}/${MAX_PICKS} selected.`}>
          {experts === null && (
            <div className="dim" style={{ fontSize: 13 }}>
              Loading experts…
            </div>
          )}
          {experts && (
            <div
              className="recspec-roster-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              {experts.map((e) => (
                <ExpertChip
                  key={e.id}
                  expert={e}
                  picked={isPicked(e)}
                  language={lang}
                  onToggle={() => {
                    if (e.supported_languages.includes(lang)) togglePick(e);
                  }}
                />
              ))}
            </div>
          )}
        </Step>

        <Step n="4" title="Configure agent" sub="Pick the role it plays in the room. Defaults are tuned to your meeting language.">
          <ConfigureAgent
            expert={pickedExperts[0] ?? null}
            archetypeId={archetypeId}
            axes={axes}
            proactiveness={proactiveness}
            channel={channel}
            frames={frames}
            onArchetypeChange={(id, baseline) => {
              setArchetypeId(id);
              setAxes(baseline);
              // Sync the legacy `tone` state so the right rail summary
              // reflects the picked archetype.
              const e = pickedExperts[0];
              if (e) setTone(archetypeToLegacyTone(e.id, id));
            }}
            onAxesChange={setAxes}
            onProactivenessChange={setProactiveness}
            onChannelChange={setChannel}
            onFramesChange={setFrames}
          />
        </Step>

        <div style={{ height: 24 }} />
      </div>

      {/* Right rail: live preview + launch */}
      <aside
        style={{
          position: "sticky",
          top: 0,
          alignSelf: "start",
          height: "100vh",
          padding: "28px 24px",
          background: "var(--paper-2)",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          overflow: "auto",
        }}
      >
        <div className="eyebrow">Bot tile preview</div>
        <BotTilePreview picked={pickedExperts} />

        <div className="eyebrow" style={{ marginTop: 8 }}>
          Configuration
        </div>
        <div
          className="card"
          style={{ padding: 14, fontSize: 12.5 }}
        >
          <RowKV k="Language" v={lang} mono />
          <RowKV k="Trigger" v={trigger === "start" ? "New Meet" : "Paste link"} />
          <RowKV k="Tone" v={tone} />
          <RowKV k="Voice" v={channels.voice} />
          <RowKV k="Q&A" v={channels.qa ? "On" : "Off"} />
          <RowKV k="Frames" v={channels.frames ? "On" : "Off"} />
          <hr className="hr" style={{ margin: "10px 0" }} />
          <div className="dim" style={{ fontSize: 11, marginBottom: 6 }}>
            Picked
          </div>
          {pickedExperts.length === 0 && (
            <div className="dimmer" style={{ fontSize: 12 }}>
              None yet.
            </div>
          )}
          {pickedExperts.map((e) => (
            <div
              key={e.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 0",
              }}
            >
              <ExpertMark expert={e} size={22} />
              <div style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 500 }}>{e.display_name}</div>
                <div className="mono dim" style={{ fontSize: 10.5 }}>
                  [{e.short_name}]
                </div>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              borderRadius: 6,
              background: "color-mix(in oklab, var(--err) 12%, var(--card))",
              border: "1px solid var(--err)",
              color: "var(--ink)",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          className="btn primary"
          disabled={!canLaunch || busy}
          onClick={submit}
          style={{
            marginTop: "auto",
            padding: "12px 16px",
            fontSize: 14,
            justifyContent: "center",
            opacity: canLaunch && !busy ? 1 : 0.5,
            cursor: canLaunch && !busy ? "pointer" : "not-allowed",
          }}
        >
          {busy
            ? "Dispatching…"
            : trigger === "start"
              ? "Start meeting & dispatch bot"
              : "Send bot to this meeting"}
          <Icon name="arrowR" size={14} />
        </button>
      </aside>
    </div>
  );
}

function Step({
  n,
  title,
  sub,
  children,
}: {
  n: string;
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-step={n}
      style={{
        padding: "26px 32px",
        borderBottom: "1px solid var(--rule)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          marginBottom: 14,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--ink-3)",
            letterSpacing: ".08em",
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: "1px solid var(--rule)",
            background: "var(--card)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {n}
        </span>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{title}</h2>
        {sub && (
          <span className="dim" style={{ fontSize: 12.5 }}>
            {sub}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function TriggerCard({
  active,
  onClick,
  icon,
  title,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 14,
        borderRadius: 8,
        border: `1px solid ${active ? "var(--ink)" : "var(--rule)"}`,
        background: active ? "var(--paper-2)" : "var(--card)",
        cursor: "pointer",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        boxShadow: active ? "var(--sh-1)" : "none",
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 6,
          background: "var(--paper-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid var(--rule)",
          flexShrink: 0,
        }}
      >
        <Icon name={icon} />
      </div>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{title}</div>
        <div className="dim" style={{ fontSize: 12, lineHeight: 1.45 }}>
          {desc}
        </div>
      </div>
    </button>
  );
}

function ExpertChip({
  expert,
  picked,
  language,
  onToggle,
}: {
  expert: ExpertSummary;
  picked: boolean;
  language: Lang;
  onToggle: () => void;
}) {
  const ok = expert.supported_languages.includes(language);
  const c = expert.color_token;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!ok}
      aria-pressed={picked}
      aria-label={expert.display_name}
      style={{
        textAlign: "left",
        padding: 16,
        borderRadius: 10,
        border: `1px solid ${picked ? `var(--${c})` : "var(--rule)"}`,
        background: picked ? `var(--${c}-tint)` : "var(--card)",
        cursor: ok ? "pointer" : "not-allowed",
        opacity: ok ? 1 : 0.45,
        boxShadow: picked
          ? `0 0 0 3px color-mix(in oklab, var(--${c}) 18%, transparent)`
          : "var(--sh-1)",
        transition: "all .15s",
        position: "relative",
        fontFamily: "inherit",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <ExpertMark expert={expert} size={44} ring={picked} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
            }}
          >
            <span
              className="serif"
              style={{ fontSize: 22, fontWeight: 400, lineHeight: 1.1 }}
            >
              {expert.display_name}
            </span>
          </div>
          <div className="mono dim" style={{ fontSize: 11, marginTop: 2 }}>
            [{expert.short_name}]
          </div>
        </div>
        {picked && (
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: `var(--${c})`,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            <Icon name="check" size={12} color="white" />
          </div>
        )}
      </div>
      <div
        style={{
          marginTop: 12,
          fontSize: 12.5,
          color: "var(--ink-2)",
          lineHeight: 1.5,
        }}
      >
        {expert.persona_summary}
      </div>
      <div
        style={{
          marginTop: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: 4 }}>
          {LANGUAGES.map((l) => (
            <LangPill
              key={l.id}
              id={l.id}
              supported={expert.supported_languages.includes(l.id)}
              active={
                l.id === language && expert.supported_languages.includes(l.id)
              }
            />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Icon name="speaker" size={12} color="var(--ink-3)" />
          <VoiceSignature expert={expert} height={12} bars={14} />
        </div>
      </div>
    </button>
  );
}

function RowKV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "4px 0",
      }}
    >
      <span className="dim">{k}</span>
      <span
        style={{
          fontWeight: 500,
          fontFamily: mono ? "var(--mono)" : "inherit",
          textTransform: mono ? "none" : "capitalize",
        }}
      >
        {v}
      </span>
    </div>
  );
}

function BotTilePreview({ picked }: { picked: ExpertSummary[] }) {
  return (
    <div
      style={{
        aspectRatio: "16/9",
        background: "#0c0c0a",
        borderRadius: 10,
        position: "relative",
        overflow: "hidden",
        border: "1px solid var(--rule-2)",
      }}
    >
      <div style={{ position: "absolute", inset: 0, display: "flex" }}>
        {picked.length === 0 && (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--ink-4)",
              fontSize: 12,
              fontFamily: "var(--mono)",
            }}
          >
            pick experts to populate the bot tile
          </div>
        )}
        {picked.map((e, i) => (
          <div
            key={e.id}
            style={{
              flex: 1,
              position: "relative",
              background: `linear-gradient(135deg, color-mix(in oklab, var(--${e.color_token}) 32%, #0c0c0a), #0c0c0a)`,
              borderRight: i < picked.length - 1 ? "1px solid #000" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <ExpertMark expert={e} size={56} />
            <div
              style={{
                color: "white",
                fontSize: 11,
                fontFamily: "var(--mono)",
                opacity: 0.7,
              }}
            >
              [{e.short_name}]
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 8,
          left: 8,
        }}
      >
        <span
          style={{
            fontFamily: "var(--mono)",
            fontSize: 10,
            color: "white",
            background: "rgba(0,0,0,.5)",
            padding: "2px 6px",
            borderRadius: 3,
          }}
        >
          RecSpec ·{" "}
          {picked.length === 0 ? "—" : picked.map((e) => e.short_name).join(" / ")}
        </span>
      </div>
    </div>
  );
}

function pinFor(e: ExpertSummary): string {
  return `${e.id}@${e.version}`;
}

// ─── ConfigureAgent ────────────────────────────────────────────────
// The Configure step's primary control surface — Archetype band (cards
// + Sample preview) + Behavior band (Proactiveness / Channel / Frames).
// Fine-tune disclosure (axis sliders) deferred to a follow-up
// iteration of extract-experts-to-yaml §10.

interface ConfigureAgentProps {
  expert: ExpertSummary | null;
  archetypeId: string | null;
  axes: AxisValues;
  proactiveness: Proactiveness;
  channel: Channel;
  frames: boolean;
  onArchetypeChange: (id: string, baseline: AxisValues) => void;
  onAxesChange: (next: AxisValues) => void;
  onProactivenessChange: (p: Proactiveness) => void;
  onChannelChange: (c: Channel) => void;
  onFramesChange: (f: boolean) => void;
}

function ConfigureAgent(props: ConfigureAgentProps) {
  const {
    expert,
    archetypeId,
    axes,
    proactiveness,
    channel,
    frames,
    onArchetypeChange,
    onAxesChange,
    onProactivenessChange,
    onChannelChange,
    onFramesChange,
  } = props;

  if (!expert) {
    return (
      <div className="dim" style={{ fontSize: 13 }}>
        Pick an expert above to configure its agent.
      </div>
    );
  }
  const archetypes = expert.archetypes ?? [];
  if (archetypes.length === 0) {
    return (
      <div
        style={{
          padding: 14,
          borderRadius: 8,
          border: "1px dashed var(--rule)",
          fontSize: 12.5,
        }}
        className="dim"
      >
        This expert hasn't published archetypes yet. The meeting will run
        with default settings; the worker uses the legacy tone path.
      </div>
    );
  }
  const selected = archetypes.find((a) => a.id === archetypeId) ?? archetypes[0];
  const baseline = defaultsFor(expert, selected.id);
  return (
    <AgentConfigCard expert={expert}>
      <div style={{ padding: "14px 18px" }}>
        <ArchetypeBand
          expert={expert}
          archetypes={archetypes}
          selectedId={selected.id}
          axes={axes}
          onSelect={(id, archetypeBaseline) =>
            onArchetypeChange(id, archetypeBaseline)
          }
        />
        <FineTuneDisclosure
          axes={axes}
          baseline={baseline}
          colorToken={expert.color_token}
          onAxesChange={onAxesChange}
          onReset={() => onAxesChange(baseline)}
        />
      </div>
      <div
        style={{
          padding: "14px 18px",
          borderTop: "1px solid var(--rule)",
          background: "var(--paper-2)",
        }}
      >
        <BehaviorBand
          proactiveness={proactiveness}
          channel={channel}
          frames={frames}
          onProactiveness={onProactivenessChange}
          onChannel={onChannelChange}
          onFrames={onFramesChange}
        />
      </div>
    </AgentConfigCard>
  );
}

function ArchetypeBand({
  expert,
  archetypes,
  selectedId,
  axes,
  onSelect,
}: {
  expert: ExpertSummary;
  archetypes: ArchetypeSummary[];
  selectedId: string;
  axes: AxisValues;
  onSelect: (id: string, baseline: AxisValues) => void;
}) {
  const selected = archetypes.find((a) => a.id === selectedId) ?? archetypes[0];
  const sample = previewLineFor({
    expertId: expert.id,
    archetypeId: selected.id,
    axes,
    archetypeDisplayName: selected.display_name,
  });
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Archetype · who this agent is in the room
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${archetypes.length}, minmax(0, 1fr))`,
          gap: 10,
        }}
      >
        {archetypes.map((a) => {
          const on = a.id === selectedId;
          return (
            <button
              key={a.id}
              type="button"
              aria-pressed={on}
              onClick={() => onSelect(a.id, a.baseline)}
              style={{
                padding: 12,
                textAlign: "left",
                borderRadius: 8,
                border: `${on ? "1px" : "0.5px"} solid ${on ? `var(--${expert.color_token}-c600, var(--ink))` : "var(--rule)"}`,
                background: on
                  ? `var(--${expert.color_token}-c50, var(--card))`
                  : "var(--card)",
                cursor: "pointer",
                fontFamily: "inherit",
                color: on
                  ? `var(--${expert.color_token}-c900, var(--ink))`
                  : "var(--ink)",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                {a.display_name}
              </div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                {a.one_line}
              </div>
            </button>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 12,
          padding: "10px 12px",
          borderRadius: 8,
          background: `var(--${expert.color_token}-c50, var(--paper-2))`,
          border: `1px solid var(--${expert.color_token}-c600, var(--rule))`,
          fontSize: 12.5,
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            textTransform: "uppercase",
            letterSpacing: ".06em",
            opacity: 0.7,
            marginBottom: 4,
          }}
        >
          Sample · {selected.id} · {axes.assertiveness} · {axes.responsiveness} · {axes.evidence}
        </div>
        <div>"{sample}"</div>
      </div>
    </div>
  );
}

function BehaviorBand({
  proactiveness,
  channel,
  frames,
  onProactiveness,
  onChannel,
  onFrames,
}: {
  proactiveness: Proactiveness;
  channel: Channel;
  frames: boolean;
  onProactiveness: (p: Proactiveness) => void;
  onChannel: (c: Channel) => void;
  onFrames: (f: boolean) => void;
}) {
  const channelDisabled = proactiveness === "notes-only";
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Behavior · how the agent participates in the room
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 10,
        }}
      >
        <BehaviorColumn
          header="When it flags something"
          options={[
            { value: "notes-only", label: "Notes only", desc: "Lands in your private notes." },
            { value: "hold", label: "Hold for review", desc: "Queues for your thumbs-up." },
          ]}
          value={proactiveness}
          onChange={(v) => onProactiveness(v as Proactiveness)}
        />
        <BehaviorColumn
          header={
            channelDisabled
              ? "Channel · n/a — notes only"
              : "Channel · where it lands"
          }
          options={[
            { value: "chat", label: "Chat", desc: "Meet chat · [<short>] prefix" },
            { value: "voice", label: "Voice", desc: "Spoken aloud by the bot." },
          ]}
          value={channel}
          onChange={(v) => onChannel(v as Channel)}
          disabled={channelDisabled}
        />
        <BehaviorColumn
          header="Frame capture"
          options={[
            { value: "off", label: "Off", desc: "Ignores screen shares." },
            { value: "on", label: "On", desc: "Saves diagrams, mocks, code." },
          ]}
          value={frames ? "on" : "off"}
          onChange={(v) => onFrames(v === "on")}
        />
      </div>
    </div>
  );
}

function BehaviorColumn({
  header,
  options,
  value,
  onChange,
  disabled,
}: {
  header: string;
  options: { value: string; label: string; desc: string }[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ opacity: disabled ? 0.5 : 1 }}>
      <div
        className="mono dim"
        style={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: ".06em",
          marginBottom: 6,
        }}
      >
        {header}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {options.map((o) => {
          const on = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onChange(o.value)}
              style={{
                padding: "8px 10px",
                textAlign: "left",
                borderRadius: 6,
                border: `${on ? "1px" : "0.5px"} solid ${on ? "var(--ink)" : "var(--rule)"}`,
                background: on ? "var(--ink-1, var(--card))" : "var(--card)",
                cursor: disabled ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 12.5 }}>{o.label}</div>
              <div className="dim" style={{ fontSize: 11, marginTop: 1 }}>
                {o.desc}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
